import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { ColorUtils, GetColor, Headline2, Icon, ZIndex } from 'venn-ui-kit';
import HorizontalSample from './images/upload/horizontal.png';
import MultiDateSample from './images/upload/multi-date.png';
import GridSample from './images/upload/returns-grid.png';
import DBSample from './images/upload/vertical-db.png';
import VerticalSample from './images/upload/vertical.png';
import ProFormaPortfolio from './images/upload/proforma_portfolio.png';
import HistoricalPortfolio from './images/upload/historical_portfolio.png';
import HistoricalPortfolioHorizontal from './images/upload/historical_portfolio_horizontal.png';
import { FileLayout } from './SampleData';

export const FileIcon = ({
  label,
  color,
  solid,
  multiline,
  icon,
}: {
  label?: string;
  icon?: string;
  color: string;
  solid?: boolean;
  multiline?: boolean;
}) => {
  const { Colors } = useContext(ThemeContext);
  return (
    <span className="fa-stack fa-lg">
      <StyledIcon className="fas fa-file fa-stack-2x" color={solid ? ColorUtils.opacify(color, 0.15) : Colors.White} />
      <StyledIcon className="fal fa-file fa-stack-2x" color={color} />
      {icon ? (
        <CenterIcon className={`fa fa-${icon} fa-stack-1x`} color={color} />
      ) : (
        <Label className="fa fa-stack-1x" multiline={multiline}>
          {label}
        </Label>
      )}
    </span>
  );
};

const StyledIcon = styled.i<{ color: string }>`
  color: ${(props) => props.color};
`;

const CenterIcon = styled(StyledIcon)`
  font-size: 0.7em;
  top: 0.25em;
`;

const Label = styled.i<{ multiline?: boolean }>`
  color: ${GetColor.Black};
  font-size: ${(props) => (props.multiline ? 0.3 : 0.45)}em;
  top: 2em;
  font-family: ${(props) => props.theme.Typography.fontFamily};
  line-height: normal;
  ${(props) => props.multiline && 'padding: 1em;'}
`;

const LeftFile = styled.div`
  transform: rotate(-10deg);
  font-size: 25px;
  position: relative;
  left: 40px;
  top: 10px;
`;

const MiddleFile = styled.div`
  font-size: 30px;
  z-index: ${ZIndex.Front};
`;

const RightFile = styled.div`
  transform: rotate(10deg);
  font-size: 25px;
  position: relative;
  left: -40px;
  top: 10px;
`;

const FileContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const HiddenFileContainer = styled(FileContainer)`
  display: none;
`;

const DropTitle = styled(Headline2)`
  color: ${GetColor.Primary.Dark};
  font-weight: bold;
  display: none;
`;

export const getIconForFormat = (fileLayout: FileLayout): React.ReactNode => {
  switch (fileLayout) {
    case FileLayout.VERTICAL:
      return <ImageWithMaskedGradient src={VerticalSample} alt="Vertical Format" />;
    case FileLayout.HORIZONTAL:
      return <ImageWithMaskedGradient src={HorizontalSample} alt="Horizontal Format" />;
    case FileLayout.GRID:
      return <ImageWithMaskedGradient src={GridSample} alt="Grid Format" />;
    case FileLayout.DATABASE:
      return <ImageWithMaskedGradient src={DBSample} alt="Database Format" />;
    case FileLayout.MULTIDATE:
      return <ImageWithMaskedGradient src={MultiDateSample} alt="Multiple Date Column Format" />;
    case FileLayout.MULTI_PORTFOLIO_PROFORMA:
      return <Image src={ProFormaPortfolio} alt="Pro Forma Portfolio" />;
    case FileLayout.MULTI_PORTFOLIO_HISTORICAL:
      return <Image src={HistoricalPortfolio} alt="Historical Portfolio (Vertical)" />;
    case FileLayout.MULTI_PORTFOLIO_HISTORICAL_HORIZONTAL:
      return <Image src={HistoricalPortfolioHorizontal} alt="Historical Portfolio (Horizontal)" />;
  }
  return null;
};

export const IconClasses = {
  main: 'icon-main',
  hover: 'icon-hover',
  drag: 'icon-drag',
};

export const OnlyExcel = () => {
  const { Colors } = useContext(ThemeContext);
  return (
    <>
      <FileContainer className={IconClasses.main}>
        <MiddleFile>
          <FileIcon label=".xls" color={Colors.Green} />
        </MiddleFile>
      </FileContainer>
      <HiddenFileContainer className={IconClasses.drag}>
        <MiddleFile>
          <FileIcon label=".xls" color={Colors.Green} />
        </MiddleFile>
      </HiddenFileContainer>
      <HiddenFileContainer className={IconClasses.hover}>
        <MiddleFile>
          <FileIcon label=".xls" color={Colors.Green} />
        </MiddleFile>
      </HiddenFileContainer>
      <DropTitle className={IconClasses.drag}>Drop file here</DropTitle>
    </>
  );
};

export const FilesTrifecta = () => {
  const { Colors } = useContext(ThemeContext);
  return (
    <>
      <FileContainer className={IconClasses.main}>
        <LeftFile>
          <FileIcon label=".pdf" color={Colors.Error} />
        </LeftFile>
        <MiddleFile>
          <FileIcon label=".csv" color={Colors.HighlightDark} />
        </MiddleFile>
        <RightFile>
          <FileIcon label=".xls" color={Colors.Green} />
        </RightFile>
      </FileContainer>
      <HiddenFileContainer className={IconClasses.drag}>
        <LeftFile>
          <FileIcon color={Colors.HintGrey} solid />
        </LeftFile>
        <MiddleFile>
          <FileIcon icon="upload" color={Colors.HighlightDark} solid />
        </MiddleFile>
        <RightFile>
          <FileIcon color={Colors.HintGrey} solid />
        </RightFile>
      </HiddenFileContainer>
      <HiddenFileContainer className={IconClasses.hover}>
        <LeftFile>
          <FileIcon label=".pdf" color={Colors.Error} solid />
        </LeftFile>
        <MiddleFile>
          <FileIcon label=".csv" color={Colors.HighlightDark} solid />
        </MiddleFile>
        <RightFile>
          <FileIcon label=".xls" color={Colors.Green} solid />
        </RightFile>
      </HiddenFileContainer>
      <DropTitle className={IconClasses.drag}>Drop file here</DropTitle>
    </>
  );
};

// icons for the refreshed uploader
export const AllUploadIcons = () => (
  <svg width="195" height="56" viewBox="0 0 195 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M35.1875 52.5C37.0469 52.5 38.6875 50.9688 38.6875 49V21H26.4375C23.4844 21 21.1875 18.7031 21.1875 15.75V3.5H7.1875C5.21875 3.5 3.6875 5.14062 3.6875 7V49C3.6875 50.9688 5.21875 52.5 7.1875 52.5H35.1875ZM26.4375 17.5H38.5781C38.4688 17.2812 38.3594 16.9531 38.1406 16.7344L25.4531 4.04688C25.2344 3.82812 24.9062 3.71875 24.6875 3.60938V15.75C24.6875 16.7344 25.4531 17.5 26.4375 17.5ZM7.1875 0H24.25C25.5625 0 26.9844 0.65625 27.9688 1.64062L40.5469 14.2188C41.5312 15.2031 42.1875 16.625 42.1875 17.9375V49C42.1875 52.9375 39.0156 56 35.1875 56H7.1875C3.25 56 0.1875 52.9375 0.1875 49V7C0.1875 3.17188 3.25 0 7.1875 0ZM16.4844 27.0156L21.1875 33.7969L25.7812 27.0156C26.4375 26.25 27.5312 26.0312 28.2969 26.5781C29.0625 27.125 29.2812 28.2188 28.7344 29.0938L23.2656 36.75L28.7344 44.5156C29.2812 45.3906 29.0625 46.375 28.2969 47.0312C27.4219 47.5781 26.4375 47.3594 25.7812 46.5938L21.1875 39.8125L16.4844 46.5938C15.9375 47.3594 14.8438 47.5781 13.9688 47.0312C13.2031 46.375 12.9844 45.2812 13.5312 44.5156L19 36.75L13.5312 29.0938C12.9844 28.2188 13.2031 27.125 13.9688 26.5781C14.8438 26.0312 15.9375 26.25 16.4844 27.0156ZM70.1875 52.5H75.4375V56H70.1875C66.25 56 63.1875 52.9375 63.1875 49V7C63.1875 3.17188 66.25 0 70.1875 0H87.25C88.5625 0 89.9844 0.65625 90.9688 1.64062L103.547 14.2188C104.531 15.2031 105.188 16.625 105.188 17.9375V33.25H101.688V21H89.4375C86.4844 21 84.1875 18.7031 84.1875 15.75V3.5H70.1875C68.2188 3.5 66.6875 5.14062 66.6875 7V49C66.6875 50.9688 68.2188 52.5 70.1875 52.5ZM101.578 17.5C101.469 17.2812 101.359 16.9531 101.141 16.7344L88.4531 4.04688C88.2344 3.82812 87.9062 3.71875 87.6875 3.60938V15.75C87.6875 16.7344 88.4531 17.5 89.4375 17.5H101.578ZM85.0625 38.5H86.8125C89.2188 38.5 91.1875 40.4688 91.1875 42.875V43.75C91.1875 44.7344 90.3125 45.5 89.4375 45.5C88.4531 45.5 87.6875 44.7344 87.6875 43.75V42.875C87.6875 42.4375 87.25 42 86.8125 42H85.0625C84.5156 42 84.1875 42.4375 84.1875 42.875V51.625C84.1875 52.1719 84.5156 52.5 85.0625 52.5H86.8125C87.25 52.5 87.6875 52.1719 87.6875 51.625V50.75C87.6875 49.875 88.4531 49 89.4375 49C90.3125 49 91.1875 49.875 91.1875 50.75V51.625C91.1875 54.1406 89.2188 56 86.8125 56H85.0625C82.5469 56 80.6875 54.1406 80.6875 51.625V42.875C80.6875 40.4688 82.5469 38.5 85.0625 38.5ZM99.6094 38.5H103.438C104.312 38.5 105.188 39.375 105.188 40.25C105.188 41.2344 104.312 42 103.438 42H99.6094C98.7344 42 98.1875 42.6562 98.1875 43.5312C98.1875 44.0781 98.5156 44.5156 98.9531 44.8438L103.109 46.5938C104.859 47.3594 106.062 49.2188 106.062 51.0781C106.062 53.8125 103.766 56 101.031 56H96.4375C95.4531 56 94.6875 55.2344 94.6875 54.25C94.6875 53.375 95.4531 52.5 96.4375 52.5H101.031C101.906 52.5 102.562 51.9531 102.562 51.0781C102.562 50.5312 102.234 49.9844 101.688 49.7656L97.5312 48.0156C95.7812 47.25 94.6875 45.3906 94.6875 43.5312C94.6875 40.7969 96.875 38.5 99.6094 38.5ZM110.438 38.5C111.312 38.5 112.188 39.375 112.188 40.25V43.75C112.188 46.2656 112.734 48.7812 113.938 50.9688C115.031 48.7812 115.688 46.2656 115.688 43.75V40.25C115.688 39.375 116.453 38.5 117.438 38.5C118.312 38.5 119.188 39.375 119.188 40.25V43.75C119.188 47.5781 117.984 51.2969 115.906 54.4688L115.359 55.2344C115.031 55.7812 114.484 56 113.938 56C113.281 56 112.734 55.7812 112.406 55.2344L111.859 54.4688C109.781 51.2969 108.688 47.5781 108.688 43.75V40.25C108.688 39.375 109.453 38.5 110.438 38.5ZM147.188 52.5H152.438V56H147.188C143.25 56 140.188 52.9375 140.188 49V7C140.188 3.17188 143.25 0 147.188 0H164.25C165.562 0 166.984 0.65625 167.969 1.64062L180.547 14.2188C181.531 15.2031 182.188 16.625 182.188 17.9375V33.25H178.688V21H166.438C163.484 21 161.188 18.7031 161.188 15.75V3.5H147.188C145.219 3.5 143.688 5.14062 143.688 7V49C143.688 50.9688 145.219 52.5 147.188 52.5ZM178.578 17.5C178.469 17.2812 178.359 16.9531 178.141 16.7344L165.453 4.04688C165.234 3.82812 164.906 3.71875 164.688 3.60938V15.75C164.688 16.7344 165.453 17.5 166.438 17.5H178.578ZM159.438 38.5H162.938C166.219 38.5 169.062 41.3438 169.062 44.625C169.062 48.0156 166.219 50.75 162.938 50.75H161.188V54.25C161.188 55.2344 160.312 56 159.438 56C158.453 56 157.688 55.2344 157.688 54.25V49V40.25C157.688 39.375 158.453 38.5 159.438 38.5ZM162.938 47.25C164.359 47.25 165.562 46.1562 165.562 44.625C165.562 43.2031 164.359 42 162.938 42H161.188V47.25H162.938ZM173.438 38.5H176.938C179.781 38.5 182.188 40.9062 182.188 43.75V50.75C182.188 53.7031 179.781 56 176.938 56H173.438C172.453 56 171.688 55.2344 171.688 54.25V40.25C171.688 39.375 172.453 38.5 173.438 38.5ZM176.938 52.5C177.812 52.5 178.688 51.7344 178.688 50.75V43.75C178.688 42.875 177.812 42 176.938 42H175.188V52.5H176.938ZM185.688 40.25C185.688 39.375 186.453 38.5 187.438 38.5H192.688C193.562 38.5 194.438 39.375 194.438 40.25C194.438 41.2344 193.562 42 192.688 42H189.188V45.5H192.688C193.562 45.5 194.438 46.375 194.438 47.25C194.438 48.2344 193.562 49 192.688 49H189.188V54.25C189.188 55.2344 188.312 56 187.438 56C186.453 56 185.688 55.2344 185.688 54.25V47.25V40.25Z"
      fill="#AFBACC"
    />
  </svg>
);

export const OnlyExcelIcon = () => (
  <svg width="43" height="56" viewBox="0 0 43 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M35.1875 52.5C37.0469 52.5 38.6875 50.9688 38.6875 49V21H26.4375C23.4844 21 21.1875 18.7031 21.1875 15.75V3.5H7.1875C5.21875 3.5 3.6875 5.14062 3.6875 7V49C3.6875 50.9688 5.21875 52.5 7.1875 52.5H35.1875ZM26.4375 17.5H38.5781C38.4688 17.2812 38.3594 16.9531 38.1406 16.7344L25.4531 4.04688C25.2344 3.82812 24.9062 3.71875 24.6875 3.60938V15.75C24.6875 16.7344 25.4531 17.5 26.4375 17.5ZM7.1875 0H24.25C25.5625 0 26.9844 0.65625 27.9688 1.64062L40.5469 14.2188C41.5312 15.2031 42.1875 16.625 42.1875 17.9375V49C42.1875 52.9375 39.0156 56 35.1875 56H7.1875C3.25 56 0.1875 52.9375 0.1875 49V7C0.1875 3.17188 3.25 0 7.1875 0ZM16.4844 27.0156L21.1875 33.7969L25.7812 27.0156C26.4375 26.25 27.5312 26.0312 28.2969 26.5781C29.0625 27.125 29.2812 28.2188 28.7344 29.0938L23.2656 36.75L28.7344 44.5156C29.2812 45.3906 29.0625 46.375 28.2969 47.0312C27.4219 47.5781 26.4375 47.3594 25.7812 46.5938L21.1875 39.8125L16.4844 46.5938C15.9375 47.3594 14.8438 47.5781 13.9688 47.0312C13.2031 46.375 12.9844 45.2812 13.5312 44.5156L19 36.75L13.5312 29.0938C12.9844 28.2188 13.2031 27.125 13.9688 26.5781C14.8438 26.0312 15.9375 26.25 16.4844 27.0156Z"
      fill="#AFBACC"
    />
  </svg>
);

export const ExcelAndCsvIcon = () => (
  <svg width="120" height="56" viewBox="0 0 120 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M35.6875 52.5C37.5469 52.5 39.1875 50.9688 39.1875 49V21H26.9375C23.9844 21 21.6875 18.7031 21.6875 15.75V3.5H7.6875C5.71875 3.5 4.1875 5.14062 4.1875 7V49C4.1875 50.9688 5.71875 52.5 7.6875 52.5H35.6875ZM26.9375 17.5H39.0781C38.9688 17.2812 38.8594 16.9531 38.6406 16.7344L25.9531 4.04688C25.7344 3.82812 25.4062 3.71875 25.1875 3.60938V15.75C25.1875 16.7344 25.9531 17.5 26.9375 17.5ZM7.6875 0H24.75C26.0625 0 27.4844 0.65625 28.4688 1.64062L41.0469 14.2188C42.0312 15.2031 42.6875 16.625 42.6875 17.9375V49C42.6875 52.9375 39.5156 56 35.6875 56H7.6875C3.75 56 0.6875 52.9375 0.6875 49V7C0.6875 3.17188 3.75 0 7.6875 0ZM16.9844 27.0156L21.6875 33.7969L26.2812 27.0156C26.9375 26.25 28.0312 26.0312 28.7969 26.5781C29.5625 27.125 29.7812 28.2188 29.2344 29.0938L23.7656 36.75L29.2344 44.5156C29.7812 45.3906 29.5625 46.375 28.7969 47.0312C27.9219 47.5781 26.9375 47.3594 26.2812 46.5938L21.6875 39.8125L16.9844 46.5938C16.4375 47.3594 15.3438 47.5781 14.4688 47.0312C13.7031 46.375 13.4844 45.2812 14.0312 44.5156L19.5 36.75L14.0312 29.0938C13.4844 28.2188 13.7031 27.125 14.4688 26.5781C15.3438 26.0312 16.4375 26.25 16.9844 27.0156ZM70.6875 52.5H75.9375V56H70.6875C66.75 56 63.6875 52.9375 63.6875 49V7C63.6875 3.17188 66.75 0 70.6875 0H87.75C89.0625 0 90.4844 0.65625 91.4688 1.64062L104.047 14.2188C105.031 15.2031 105.688 16.625 105.688 17.9375V33.25H102.188V21H89.9375C86.9844 21 84.6875 18.7031 84.6875 15.75V3.5H70.6875C68.7188 3.5 67.1875 5.14062 67.1875 7V49C67.1875 50.9688 68.7188 52.5 70.6875 52.5ZM102.078 17.5C101.969 17.2812 101.859 16.9531 101.641 16.7344L88.9531 4.04688C88.7344 3.82812 88.4062 3.71875 88.1875 3.60938V15.75C88.1875 16.7344 88.9531 17.5 89.9375 17.5H102.078ZM85.5625 38.5H87.3125C89.7188 38.5 91.6875 40.4688 91.6875 42.875V43.75C91.6875 44.7344 90.8125 45.5 89.9375 45.5C88.9531 45.5 88.1875 44.7344 88.1875 43.75V42.875C88.1875 42.4375 87.75 42 87.3125 42H85.5625C85.0156 42 84.6875 42.4375 84.6875 42.875V51.625C84.6875 52.1719 85.0156 52.5 85.5625 52.5H87.3125C87.75 52.5 88.1875 52.1719 88.1875 51.625V50.75C88.1875 49.875 88.9531 49 89.9375 49C90.8125 49 91.6875 49.875 91.6875 50.75V51.625C91.6875 54.1406 89.7188 56 87.3125 56H85.5625C83.0469 56 81.1875 54.1406 81.1875 51.625V42.875C81.1875 40.4688 83.0469 38.5 85.5625 38.5ZM100.109 38.5H103.938C104.812 38.5 105.688 39.375 105.688 40.25C105.688 41.2344 104.812 42 103.938 42H100.109C99.2344 42 98.6875 42.6562 98.6875 43.5312C98.6875 44.0781 99.0156 44.5156 99.4531 44.8438L103.609 46.5938C105.359 47.3594 106.562 49.2188 106.562 51.0781C106.562 53.8125 104.266 56 101.531 56H96.9375C95.9531 56 95.1875 55.2344 95.1875 54.25C95.1875 53.375 95.9531 52.5 96.9375 52.5H101.531C102.406 52.5 103.062 51.9531 103.062 51.0781C103.062 50.5312 102.734 49.9844 102.188 49.7656L98.0312 48.0156C96.2812 47.25 95.1875 45.3906 95.1875 43.5312C95.1875 40.7969 97.375 38.5 100.109 38.5ZM110.938 38.5C111.812 38.5 112.688 39.375 112.688 40.25V43.75C112.688 46.2656 113.234 48.7812 114.438 50.9688C115.531 48.7812 116.188 46.2656 116.188 43.75V40.25C116.188 39.375 116.953 38.5 117.938 38.5C118.812 38.5 119.688 39.375 119.688 40.25V43.75C119.688 47.5781 118.484 51.2969 116.406 54.4688L115.859 55.2344C115.531 55.7812 114.984 56 114.438 56C113.781 56 113.234 55.7812 112.906 55.2344L112.359 54.4688C110.281 51.2969 109.188 47.5781 109.188 43.75V40.25C109.188 39.375 109.953 38.5 110.938 38.5Z"
      fill="#AFBACC"
    />
  </svg>
);

const ImageWithMaskedGradient = styled.img`
  width: 100%;
  -webkit-backface-visibility: hidden;
  -ms-transform: translateZ(0); /* IE 9 */
  -webkit-transform: translateZ(0); /* Chrome, Safari, Opera */
  transform: translateZ(0);
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.1));
  -webkit-mask-image: -webkit-gradient(linear, left top, left bottom, from(rgba(0, 0, 0, 1)), to(rgba(0, 0, 0, 0.1)));
`;

const Image = styled.img`
  width: 100%;
`;

const Columns = styled(Icon)`
  color: ${GetColor.Green};
  position: relative;
  left: 30px;
  top: -20px;
`;

const Rows = styled(Icon)`
  color: ${GetColor.HighlightDark};
  transform: rotate(90deg);
  position: relative;
  left: -30px;
  top: 20px;
`;

const GridContainer = styled.div`
  font-size: 170px;
  opacity: 0.1;
  position: absolute;
  z-index: ${ZIndex.Background};
  top: 10px;
`;

export const OldUploaderBackground = () => (
  <GridContainer>
    <Columns type="line-columns" prefix="fad" />
    <Rows type="line-columns" prefix="fad" />
  </GridContainer>
);
